import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";

type Props = {
  current: number;
  target: number;
};

/**
 * The mobile `WorkoutCaloriesWidget`: a framed card with "Calories", a thin
 * progress bar of burned over target, and "x / y Kcal". The frame turns
 * dark while in progress and green once the target is met.
 */
const CaloriesWidget = ({ current, target }: Props) => {
  const theme: Theme = useTheme();
  const isInProgress = current > 0 && current < target;
  const isCompleted = target > 0 && current >= target;
  const percent = target > 0 ? Math.min(100, (current / target) * 100) : 0;

  return (
    <Box sx={styles.container(theme, isInProgress, isCompleted)}>
      <Box sx={styles.title(theme, isInProgress, isCompleted)}>Calories</Box>
      <Box sx={styles.bar(theme)}>
        <Box sx={styles.fill(theme, percent)} />
      </Box>
      <Box sx={styles.value(theme)}>
        {Math.round(current)} / {Math.round(target)} Kcal
      </Box>
    </Box>
  );
};

export default CaloriesWidget;

const styles = {
  container: (theme: Theme, inProgress: boolean, completed: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${
      completed
        ? theme.colorTokens.S[20]
        : inProgress
        ? theme.colorTokens.N[90]
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: completed ? theme.colorTokens.S[10] : theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  title: (theme: Theme, inProgress: boolean, completed: boolean) => ({
    color: completed
      ? theme.colorTokens.S[90]
      : inProgress
      ? theme.colors_ds.NEUTRAL_TEXT_STRONG
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  bar: (theme: Theme) => ({
    height: "6px",
    margin: "8px 0",
    borderRadius: "38px",
    backgroundColor: theme.colorTokens.S[10],
    overflow: "hidden",
  }),
  fill: (theme: Theme, percent: number) => ({
    height: "6px",
    width: `${percent}%`,
    borderRadius: "38px",
    backgroundColor: theme.colorTokens.S[60],
  }),
  value: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
